import { useCallback, useEffect, useRef, useState } from 'react'
import { times } from 'lodash'
import moment from 'moment'

// BL
import { UploadScreenProps } from './index'
import {
  buildDuplicatePages,
  UploadDuplicatePageModel,
} from './duplicateHelpers'
import { Transaction } from '@/reducers/admin/allTransactions.slice'
import { userUploadDocument } from '@/actions/awsActions'
import { parseErrorFromCatch } from '@/utils/errorHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { createMultipleManualTransactions } from '@/features/transactions/transactions.slice'

// UI
import { Button, Text, Dialog } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { UploadBackButton } from '../UploadBackButton'
import { UPLOAD_COPY } from '@/features/transactions/copyConstants'

const UploadDuplicateRow = ({ page }: { page: UploadDuplicatePageModel }) => {
  const dateFormatted = page.transaction
    ? moment.utc(page.transaction.date).format(DATE_FORMATS.DISPLAY_SHORT)
    : ''

  return (
    <div className="flex min-h-[85px] my-4 p-4 w-full rounded-md">
      <Text className="flex-1 text-left">{dateFormatted}</Text>
      <Text className="flex-2 text-left">{page.transaction?.description}</Text>
      <Text className="flex-1 text-right font-bold">
        $ {page.transaction?.amountInDollars ?? ''}
      </Text>
    </div>
  )
}

/**
 * Walks the user through each duplicate group (database duplicates first, then
 * within-spreadsheet duplicates), one page per group:
 * - database-dup page: "keep" adds the row once to validTransactions unless the
 *   group also repeats within the spreadsheet (its spreadsheet page decides the
 *   count); "remove" adds nothing and drops the group's spreadsheet page.
 * - spreadsheet-dup page: "keep" adds all n occurrences; "remove" collapses
 *   them to 1.
 * When no pages remain, validTransactions are saved and the spreadsheet file
 * (if any) is uploaded.
 */

const UploadDuplicateScreen = ({
  goToScreenWithData,
  data,
  finishFlow,
  onUploadComplete,
}: UploadScreenProps) => {
  const dispatch = useAppDispatch()
  const [init, setInit] = useState(false)
  const [currentPage, setCurrentPage] = useState<UploadDuplicatePageModel>()
  const [currentPageIndex, setCurrentPageIndex] = useState<number>()
  const [pages, setPages] = useState<Array<UploadDuplicatePageModel>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<null | string>()
  const saveStartedRef = useRef(false)
  const currentUser = useReselector(getCurrentUser)
  const { validTransactions, duplicateGroups, spreadsheetFile } = data || {}

  const performSave = useCallback(async () => {
    setSaving(true)

    try {
      if (validTransactions && validTransactions.length > 0) {
        const result =
          await createMultipleManualTransactions(validTransactions)(dispatch)
        const payload = result.map((t: Transaction) => ({
          id: Number(t.id),
          date: t.date,
          description: t.description,
          amountInDollars: centsToDollars(t.amountInCents).toString(),
        }))
        if (result) {
          if (spreadsheetFile && currentUser) {
            const docRes = await dispatch(
              userUploadDocument({
                file: spreadsheetFile,
                user: currentUser,
                documentType: 'other',
              })
            )
            if (docRes && 'error' in docRes) {
              throw new Error(docRes.error)
            }
          }
          onUploadComplete?.({ success: true, data: payload })
        } else {
          onUploadComplete?.({ success: false })
        }
      }

      finishFlow()
    } catch (err) {
      setError(parseErrorFromCatch(err))
    }

    setSaving(false)
  }, [
    validTransactions,
    finishFlow,
    dispatch,
    spreadsheetFile,
    currentUser,
    onUploadComplete,
  ])

  // 1
  useEffect(() => {
    // Data has been set, populate the pages
    if (duplicateGroups && duplicateGroups.length > 0) {
      setPages(buildDuplicatePages(duplicateGroups))
    }
  }, [duplicateGroups, setPages])

  // 2
  useEffect(() => {
    // Pages has been set, set the current index (which renders it)
    if (pages.length > 0 && !init) {
      setInit(true)
      setCurrentPageIndex(0)
    }
  }, [pages, init, setCurrentPageIndex, setInit])

  // 3
  useEffect(() => {
    if (typeof currentPageIndex === 'undefined') {
      return
    }

    // Render the current page

    if (currentPageIndex < pages.length) {
      // The page index has changed, navigate to next page
      setCurrentPage(pages[currentPageIndex])
    } else if (!saveStartedRef.current) {
      // performSave's redux dispatches re-render and re-fire this effect
      // before finishFlow unmounts; the ref keeps the save single-shot
      saveStartedRef.current = true
      performSave()
    }
  }, [currentPageIndex, pages, performSave, setCurrentPage])

  const goToLandingScreen = () => goToScreenWithData({ index: 0 })
  const goToNextPage = () =>
    setCurrentPageIndex((prev) => (typeof prev !== 'undefined' ? prev + 1 : 0))

  const groupHasSpreadsheetPage = (groupId: string) =>
    pages.some((p) => p.type === 'spreadsheet-dup' && p.groupId === groupId)

  const onRemoveDuplicate = () => {
    if (currentPage?.type === 'database-dup') {
      if (groupHasSpreadsheetPage(currentPage.groupId)) {
        setPages(
          pages.filter(
            (p) =>
              !(
                p.type === 'spreadsheet-dup' &&
                p.groupId === currentPage.groupId
              )
          )
        )
      }
    } else if (currentPage?.type === 'spreadsheet-dup') {
      // Only add 1 ManualTransactionModel to validTransactions
      validTransactions?.push(currentPage.transaction)
    }

    goToNextPage()
  }

  const onKeepDuplicate = () => {
    if (currentPage?.type === 'database-dup' && validTransactions) {
      // The group's spreadsheet page (if any) decides how many copies to keep
      if (!groupHasSpreadsheetPage(currentPage.groupId)) {
        validTransactions.push(currentPage.transaction)
      }
    } else if (currentPage?.type === 'spreadsheet-dup' && validTransactions) {
      // Add n # of ManualTransactionModel to validTransactions
      for (let x = 0; x < currentPage.numOccurrencesToShow; x++) {
        validTransactions.push(currentPage.transaction)
      }
    }

    goToNextPage()
  }

  const titleText =
    currentPageIndex && currentPageIndex >= pages.length
      ? 'Saving transactions ...'
      : `Review potential duplicates (${
          typeof currentPageIndex !== 'undefined' ? currentPageIndex + 1 : 0
        } of ${pages.length})`

  const subtitle =
    currentPage?.type === 'database-dup'
      ? UPLOAD_COPY.DUPLICATE_DATABASE_SUBTITLE
      : UPLOAD_COPY.DUPLICATE_SPREADSHEET_SUBTITLE

  const removeButtonText =
    currentPage?.type === 'database-dup'
      ? 'Yes, remove duplicate'
      : 'Yes, remove duplicates'

  const keepButtonText =
    currentPage?.type === 'database-dup' ? 'No, keep it' : 'No, keep them'

  return (
    <>
      <Dialog.Title />
      <Dialog.Content>
        <UploadBackButton disabled={saving} onClick={goToLandingScreen} />
        <div
          style={{ marginTop: '1.25em', fontWeight: 'bold' }}
          className="mt-2 font-bold"
        >
          <Text variant={TypographyStyles.H2}>{titleText}</Text>
          <Text variant={TypographyStyles.BODY_SM}>{subtitle}</Text>
        </div>
        {currentPage &&
          times(currentPage.numOccurrencesToShow).map((num) => (
            <UploadDuplicateRow key={`dup-row-${num}`} page={currentPage} />
          ))}
        {error && <Text color={TextColors.DANGER}>{error}</Text>}

        <div className="flex gap-4 mt-4">
          <Button
            disabled={saving}
            onClick={onKeepDuplicate}
            size="lg"
            className="w-full flex-1"
          >
            {keepButtonText}
          </Button>
          <Button
            disabled={saving}
            onClick={onRemoveDuplicate}
            size="lg"
            className="w-full flex-1"
          >
            {removeButtonText}
          </Button>
        </div>
      </Dialog.Content>
    </>
  )
}

export { UploadDuplicateScreen }
